import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from 'sonner';
import { api } from '../lib/api';

export default function ChangePassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, updateSession } = useAuth();

  // Überprüfe, ob der Benutzer sein Passwort ändern muss
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!user.mustChangePassword) {
      // Wenn keine Passwortänderung erforderlich ist, zurück zum Dashboard
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/leads');
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Die Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Das Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    // Überprüfe Passwortkomplexität
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      toast.error('Das Passwort muss mindestens einen Großbuchstaben, einen Kleinbuchstaben, eine Zahl und ein Sonderzeichen enthalten');
      return;
    }

    setLoading(true);
    try {
      await api.changePassword(newPassword);
      
      // Aktualisiere den User-Status im Context
      if (updateSession) {
        updateSession({
          ...user,
          mustChangePassword: false
        });
      }

      toast.success('Passwort wurde erfolgreich geändert');
      
      // Leite weiter basierend auf der Benutzerrolle
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/leads');
      }
    } catch (error) {
      console.error('Fehler beim Ändern des Passworts:', error);
      toast.error('Passwort konnte nicht geändert werden');
    } finally {
      setLoading(false);
    }
  };

  if (!user?.mustChangePassword) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <Card className="w-[400px]">
        <CardHeader>
          <div className="flex flex-col items-center">
            <img src="/logo.webp" alt="Logo" className="w-32 mb-4" />
            <CardTitle className="text-2xl text-center">Passwort ändern</CardTitle>
            <p className="text-sm text-gray-500 mt-2 text-center">
              Bitte ändern Sie Ihr temporäres Passwort, um fortzufahren
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="Neues Passwort"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
              <p className="text-xs text-gray-500">
                Mindestens 8 Zeichen, ein Großbuchstabe, ein Kleinbuchstabe, eine Zahl und ein Sonderzeichen
              </p>
            </div>
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="Passwort bestätigen"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Wird geändert...' : 'Passwort ändern'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
} 